import _ from 'lodash';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { Modal } from 'antd';
import AppStore from 'stores/App';

const PitstopModal = ({
  id,
  form,
  failureMessage,
  notifyMessage,
  children,
  visible,
  confirmOkMessage,
  okText,
  onOk,
  clearExtraFields,
  onCancel,
  successMessage,
  title,
  width,
  centered,
  afterClose,
  isManualClose = false,
  cancelButtonProps = {},
  closable = false,
  ...otherProps
}) => {
  useEffect(() => {
    if (!id) {
      throw new Error('prop id is needed for PitstopModal');
    }
    if (!AppStore.openModals.has(id)) {
      AppStore.openModals.set(id, false);
    }
  }, [id]);

  useEffect(() => {
    if (visible !== undefined) {
      AppStore.openModals.set(id, visible);
    }
  }, [visible, id]);

  const showSuccessMessage = () => {
    if (successMessage) {
      const message = successMessage || 'Action successful';
      AppStore.addSuccess(message);
    }
  };

  const showFailureMessage = () => {
    if (failureMessage) {
      const message = failureMessage || 'Action failed';
      AppStore.addError(message);
      notifyMessage && AppStore.addNotification(notifyMessage);
    }
  };

  const handleOk = async () => {
    try {
      if (onOk) {
        const onOkResult = await onOk();
        if (onOkResult === false) return;
        showSuccessMessage();
        if (!isManualClose) {
          AppStore.openModals.set(id, false);
        }
      }
    } catch {
      showFailureMessage();
    }
  };

  const confirmOnOk = async () => {
    if (!confirmOkMessage) {
      await handleOk();
      return;
    }
    Modal.confirm({
      title: confirmOkMessage,
      centered,
      onOk: async () => {
        await handleOk();
      },
    });
  };

  const hide = () => {
    if (onCancel) {
      onCancel();
      clearExtraFields && clearExtraFields();
    }
    form && form.resetFields();
    AppStore.openModals.set(id, false);
  };

  if (_.isNil(id)) {
    return null;
  }

  return (
    <Modal
      open={AppStore.openModals.get(id)}
      title={title}
      width={width}
      okText={okText}
      onCancel={hide}
      onOk={confirmOnOk}
      closable={closable}
      centered={centered}
      afterClose={afterClose}
      cancelButtonProps={cancelButtonProps}
      {...otherProps}
    >
      {children}
    </Modal>
  );
};

PitstopModal.propTypes = {
  form: PropTypes.object,
  failureMessage: PropTypes.string,
  notifyMessage: PropTypes.string,
  children: PropTypes.node.isRequired,
  visible: PropTypes.bool,
  confirmOkMessage: PropTypes.string,
  okText: PropTypes.string,
  onOk: PropTypes.func,
  clearExtraFields: PropTypes.func,
  onCancel: PropTypes.func,
  id: PropTypes.string.isRequired,
  successMessage: PropTypes.string,
  title: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  centered: PropTypes.bool,
  afterClose: PropTypes.func,
  isManualClose: PropTypes.bool,
  cancelButtonProps: PropTypes.object,
  closable: PropTypes.bool,
};

export default observer(PitstopModal);
